import { useEffect, useRef } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';

export function ScrollToTop() {
  const { pathname, hash } = useLocation();
  const navigationType = useNavigationType();
  const scrollPositions = useRef<Record<string, number>>({});
  const prevPathname = useRef(pathname);

  // Save scroll position before leaving a page
  useEffect(() => {
    const handleBeforeNavigate = () => {
      scrollPositions.current[prevPathname.current] = window.scrollY;
    };

    // Save on every scroll to capture latest position
    const handleScroll = () => {
      scrollPositions.current[pathname] = window.scrollY;
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [pathname]);

  useEffect(() => {
    // Update previous pathname
    prevPathname.current = pathname;

    if (hash) {
      // If there's a hash, scroll to that element
      const id = hash.replace('#', '');
      setTimeout(() => {
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    } else if (navigationType === 'POP') {
      // Browser back/forward: restore saved scroll position
      const savedPosition = scrollPositions.current[pathname];
      if (savedPosition !== undefined) {
        // Wait for page render then restore
        requestAnimationFrame(() => {
          setTimeout(() => {
            window.scrollTo({ top: savedPosition, behavior: 'instant' });
          }, 50);
        });
      }
    } else {
      // PUSH or REPLACE navigation: scroll to top
      window.scrollTo({ top: 0, behavior: 'instant' });
    }
  }, [pathname, hash, navigationType]);

  return null;
}
